import { otaLog } from '~/services/ota/OtaDebugLogger';

const ANDROID_DECODER_SETTLE_MS = 150;

function isAndroidPlayer(): boolean {
  const platform = (
    window as unknown as { __effluxPlatform?: { platform?: string } }
  ).__effluxPlatform?.platform;
  return platform === 'android';
}

/**
 * Pausa e desmonta <video> antes de reload — evita SIGSEGV no WebView/Chromium.
 *
 * @returns quantos `<video>` foram desmontados. Zero significa que não havia nada pra assentar,
 *   e é o que permite pular o delay (vide {@link getSafeReloadDelayMs}).
 */
export async function preparePageForReload(): Promise<number> {
  const videos = Array.from(document.querySelectorAll('video'));
  for (const video of videos) {
    try {
      video.pause();
      video.removeAttribute('src');
      video.load();
    } catch {
      /* best-effort */
    }
  }
  if (isAndroidPlayer() && videos.length > 0) {
    await new Promise((resolve) =>
      setTimeout(resolve, ANDROID_DECODER_SETTLE_MS),
    );
  }
  otaLog.info('safePageReload: videos torn down', {
    count: videos.length,
    android: isAndroidPlayer(),
  });
  return videos.length;
}

/**
 * Espera entre o teardown e o `location.reload()`.
 *
 * <p>🔴 `videoCount === 0` → **0 ms** (2026-07-28). O delay existe pra dar tempo ao decoder de
 * assentar depois de soltar um `<video>` — o Tab A7 crashava em `libmonochrome.so` sem isso. Se não
 * havia `<video>` na página, não há decoder DOM assentando e a espera é tempo morto puro.
 *
 * <p>É exatamente o caso do Tizen com backend AVPlay: o vídeo vive num `<object>`, então
 * `querySelectorAll('video')` devolve ZERO e os 200 ms eram cobrados sem proteger nada. Medido: o
 * preto do reload por wrap de loop era ~2,24 s, dos quais 200 ms eram esta espera.
 *
 * <p>Condicionar à CONTAGEM, e não à plataforma, mantém a proteção onde ela importa (qualquer
 * plataforma que de fato tenha `<video>`) sem branch de plataforma no código de app — o
 * desacoplamento validado pelo Semgrep continua intacto.
 *
 * @param videoCount quantos `<video>` foram desmontados. Omitido → assume o pior caso (há vídeo),
 *   que é o que o RemoteOps reporta como `scheduled_in_ms` antes de saber a contagem.
 */
export function getSafeReloadDelayMs(videoCount?: number): number {
  if (videoCount === 0) return 0;
  return isAndroidPlayer() ? 800 : 200;
}

/** Reload com teardown de mídia — Tab A7 crashava em libmonochrome.so sem isso. */
export async function executeSafePageReload(cmdId?: string): Promise<void> {
  const videoCount = await preparePageForReload();
  const delayMs = getSafeReloadDelayMs(videoCount);
  if (delayMs > 0) {
    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }
  otaLog.info('safePageReload: reload', {
    cmdId: cmdId ?? '?',
    delayMs,
    videoCount,
  });
  window.location.reload();
}
